import { format, isMatch } from 'date-fns';
import { isBoolean, memoize, omit } from 'lodash';

import { request } from '@swf/core/api/request';
import type { FeatureFlag } from '@swf/core/enums';
import { Currency } from '@swf/core/enums';
import type { Feature } from '@swf/core/models/feature';
import type { Store } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Error } from '@swf/core/types/Response';
import type { DepositPayload, Rates } from '@swf/first';

import type { PaySystem, PaySystems, SavedCard, SavedCards } from '../types/Cashier';
import { CardType } from '../types/cardType';
import { DepositStatus, TransactionStatus } from '../types/status';

import {
  TXTCLI_Social_Insurance_NumberX_wrong_ELL,
  TXTCLI_The_voucher_code_you_have_ente_ELL,
  TXTWCL_The_card_number_entered_is_not_ELL
} from './cashier.messages.json';

export const conversionRateBase = 100000000;

export const dollarsToCents = (dollars: number): number => Number((dollars * 100).toFixed());

export const trimWithoutRounding = (value: number, decimalDigits = 2): number => {
  const numberToString = value.toString();

  return numberToString.indexOf('.') > -1
    ? Number(numberToString.substring(0, numberToString.indexOf('.') + (decimalDigits + 1)))
    : value;
};

export const convertAmountByRate = (
  amount: number,
  rate: number,
  currency: Currency,
  inverted?: boolean,
  decimalDigits?: number
): number => {
  const trimmedAmount = trimWithoutRounding(amount);
  const result = Number(
    inverted
      ? (trimmedAmount * conversionRateBase) / rate
      : (trimmedAmount * rate) / conversionRateBase
  );

  return isZeroExponentCurrency(currency)
    ? Math.ceil(result)
    : trimWithoutRounding(result, decimalDigits);
};

export const formatExpiryDate = (expDate: number | undefined) => {
  if (expDate) {
    return isMatch(expDate.toString(), 'yyyyMM') ? expDate.toString() : format(expDate, 'yyyyMM');
  }
};

export const getCurrencyRate = (rates: Rates, currency: Currency) => {
  const currencyRate = rates.find(({ curr }) => curr === currency);

  return currencyRate ? currencyRate.rate : -1;
};

export const sanitizeDeposit = (payload: DepositPayload) =>
  omit(payload, ['accountNumber', 'cvn', 'expiryDate']);

export const getPaySystemName = (paySystems: PaySystems, type: CardType) => {
  let paySystemName = '';
  const paySystem = paySystems.find(
    ({ cardType }: { cardType: CardType }) => cardType === type
  ) as PaySystem;
  if (paySystem) {
    const { i18nSystemName = '' } = paySystem;
    paySystemName = i18nSystemName;
  }
  return paySystemName;
};

export const getSavedMethod = (savedCards: SavedCards, savedCardInfoId?: number): SavedCard[] =>
  savedCards.filter(({ savedCard: { cardInfoId } }) => cardInfoId === savedCardInfoId);

export const getFeatureFlags = async ({ getState, dispatch }: Store, flags: FeatureFlag[]) => {
  const buffer: FeatureFlag[] = [];
  const existingFlags: Partial<Record<FeatureFlag, boolean>> = {};
  const state = getState();
  const options = select.site.getRequestOptions(state);

  flags.forEach((flag) => {
    const value = select.feature.check(state, flag);

    if (isBoolean(value)) {
      buffer.push(flag);
      existingFlags[flag] = value;
    }
  });

  const requiredFlags = flags.filter((flag) => !buffer.includes(flag));

  if (requiredFlags.length) {
    const { body: features } = await request<Feature>({
      ...options,
      service: 'features.check',
      body: { features: requiredFlags }
    });
    dispatch.feature.update(features);

    return { ...features, ...existingFlags };
  }

  return existingFlags;
};

export const getDepositStatus = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.APPROVED:
      return DepositStatus.COMPLETED;
    case TransactionStatus.REJECTED:
      return DepositStatus.REJECTED;
    default:
      return DepositStatus.TIMEOUT;
  }
};

export const mapErrorCodes = (code: Error['code']) => {
  const mappedMessage = {
    TXTWCL_The_card_number_entered_is_not_ELL,
    TXTCLI_Social_Insurance_NumberX_wrong_ELL,
    TXTCLI_The_voucher_code_you_have_ente_ELL
  };

  return code && mappedMessage[code]?.defaultMessage;
};

export const zeroExponentCurrencies = [
  Currency.ChileanPeso,
  Currency.HungarianForint,
  Currency.JapaneseYen
];

export const isZeroExponentCurrency = memoize((currency: Currency) =>
  zeroExponentCurrencies.includes(currency)
);

export const trimAsterisks = (text: string) => text.replace(/\*{4,}/g, '••••').replace(/\*/g, '•');

export const showLogoInsideInput = memoize((type: CardType) =>
  [CardType.Visa, CardType.MasterCard, CardType.Solo, CardType.Astropay].includes(type)
);
